import React, { ReactElement, useState } from 'react';
import styled from '@emotion/styled';
import {
  ListboxInput,
  ListboxButton,
  ListboxPopover,
  ListboxList,
  ListboxOption,
} from '@reach/listbox';
import '@reach/listbox/styles.css';
import { TriangleDownIcon } from '@primer/octicons-react';

import { PayloadByType, BranchSwitchModus, History } from 'use-flexible-undo';
import { getLastItem, formatTime, useInterval } from './util';
import { getCurrentBranch, isUndoPossible, isRedoPossible } from './helpers';

interface BranchNavProps<PBT extends PayloadByType> {
  history: History<PBT>;
  switchToBranch: (branchId: string, travelTo?: BranchSwitchModus) => void;
  undo: () => void;
  redo: () => void;
}

export const BranchNav = <PBT extends PayloadByType>({
  history,
  switchToBranch,
  undo,
  redo,
}: BranchNavProps<PBT>): ReactElement | null => {
  const [now, setNow] = useState(new Date());
  useInterval(() => setNow(new Date()), 5000);
  const branchList = Object.values(history.branches).sort(
    (a, b) =>
      getLastItem(b.stack).created.getTime() -
      getLastItem(a.stack).created.getTime()
  );
  const currentBranch = getCurrentBranch(history);

  const canUndo = isUndoPossible(history);

  const canRedo = isRedoPossible(history);

  return (
    <Root>
      <ListboxStyled
        disabled={branchList.length === 1}
        value={currentBranch.id}
        onChange={id =>
          id !== currentBranch.id && switchToBranch(id, 'HEAD_OF_BRANCH')
        }
      >
        <ListboxButton arrow={<TriangleDownIcon size={16} />} />
        <ListboxPopover
          style={{
            padding: 0,
            border: 0,
            outline: 'none',
            zIndex: 2,
          }}
        >
          <ListboxListStyled>
            {branchList.map(b => (
              <ListboxOptionStyled
                key={b.id}
                value={b.id}
                label={`branch ${b.number}`}
              >
                {`branch ${b.number} (size ${
                  b.parent
                    ? b.parent.position.globalIndex + b.stack.length + 1
                    : b.stack.length
                }, ${formatTime(getLastItem(b.stack).created, now)})`}
              </ListboxOptionStyled>
            ))}
          </ListboxListStyled>
        </ListboxPopover>
      </ListboxStyled>
      <Buttons>
        <button disabled={!canUndo} onClick={() => undo()}>
          undo
          <span
            style={{
              display: 'inline-block',
              marginLeft: '8px',
              transform: 'rotate(-90deg) scale(1.5)',
              opacity: 0.5,
            }}
          >
            &#9100;
          </span>
        </button>
        <button disabled={!canRedo} onClick={() => redo()}>
          <span
            style={{
              display: 'inline-block',
              marginRight: '8px',
              transform: 'rotate(90deg) scale(1.5)',
              opacity: 0.5,
            }}
          >
            &#9100;
          </span>
          redo
        </button>
      </Buttons>
    </Root>
  );
};

const Root = styled.div`
  padding: 16px;
  border-bottom: 1px solid #aaa;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  button {
    font-size: 14px;
    padding: 4px 8px;
    border-radius: 2px;
    font-family: Verdana, sans-serif;
    &:focus {
      outline: 1px solid #48a7f6;
    }
    display: inline-block;
    border: 1px solid #aaa;
    &:hover:not(:disabled) {
      background: #f7f8fa;
    }
    cursor: pointer;
    :disabled {
      border-color: #ddd;
      cursor: inherit;
    }
    min-width: 95px;
  }
`;

const Buttons = styled.div`
  display: flex;
  margin-top: 16px;
  justify-content: space-between;
  > button {
    flex: 1 1 0;
    &:first-of-type {
      margin-right: 16px;
    }
  }
`;

const ListboxStyled = styled(ListboxInput)`
  [data-reach-listbox-button] {
    box-sizing: border-box;
    width: 100%;
    height: 30px;
    background: white;
    padding: 4px 8px;
    font-size: 14px;
    border-radius: 2px;
    border-color: #aaa;
    cursor: pointer;
    &[aria-disabled] {
      cursor: default;
    }
    &:focus {
      outline: 1px solid #48a7f6;
    }
    &:hover {
      background: #f7f8fa;
    }
  }
`;

const ListboxListStyled = styled(ListboxList)`
  padding: 0;
  border: 1px solid #aaa;
  box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.1);
`;

const ListboxOptionStyled = styled(ListboxOption)`
  font-family: Verdana, sans-serif;
  font-size: 14px;
  cursor: pointer;
  padding: 8px 16px;
  border: 0;
  max-width: 100%;
  &[aria-selected='true'] {
    background: #f7f8fa;
    color: black;
  }
  &[data-current] {
    color: white;
    background: #48a7f6;
    font-weight: normal;
    cursor: default;
  }
`;
